import { z } from "zod";
import { inviteUserSchema } from "./invites";

/**
 * Send a 1:1 private message. Text chat (DMs and table chat) rides the
 * authoritative WebSocket, so it keeps working on filtered Iranian networks;
 * optional LiveKit voice is a separate, degrade-gracefully layer.
 */
export const sendDmSchema = z.object({
  toUserId: z.string().min(1),
  body: z.string().trim().min(1).max(2000),
});
export type SendDmInput = z.infer<typeof sendDmSchema>;

/** A lightweight table-chat message / emote sent during a game. */
export const tableChatSchema = z.object({
  roomId: z.string().min(1),
  body: z.string().trim().min(1).max(500),
});
export type TableChatInput = z.infer<typeof tableChatSchema>;

/**
 * A relayed reference to an already-uploaded voice note: the api validates +
 * stores the clip bytes (`POST /voice`) and returns its id; chat only carries
 * this reference (cuid-ish id + denormalized clip length, capped at 60s).
 */
export const voiceRefSchema = z.object({
  voiceId: z.string().regex(/^[a-z0-9]{1,64}$/),
  durationMs: z.number().int().positive().max(60000),
});
export type VoiceRefInput = z.infer<typeof voiceRefSchema>;

/** Send a voice note as a 1:1 private message. */
export const sendVoiceDmSchema = z.object({
  toUserId: z.string().min(1),
  ...voiceRefSchema.shape,
});
export type SendVoiceDmInput = z.infer<typeof sendVoiceDmSchema>;

/** Send a voice note into a game's table chat. */
export const tableVoiceSchema = z.object({
  roomId: z.string().min(1),
  ...voiceRefSchema.shape,
});
export type TableVoiceInput = z.infer<typeof tableVoiceSchema>;

/**
 * `GET /me/dm-unread` — the caller's unread direct messages, one row per
 * conversation that HAS unread messages (`unread` ≥ 1; fully-read threads are
 * omitted). `peerId` is the other participant's app user id — the same id the
 * chat list keys its rows on — so the client joins counts to rows directly.
 * Unread state is per-message `readAt` server-side: a message counts until the
 * recipient's DM room marks it read (thread open, or delivery while open).
 */
export const dmUnreadThreadSchema = z.object({
  peerId: z.string(),
  unread: z.number().int().positive(),
});
export type DmUnreadThread = z.infer<typeof dmUnreadThreadSchema>;

export const dmUnreadResponseSchema = z.object({
  threads: z.array(dmUnreadThreadSchema),
});
export type DmUnreadResponse = z.infer<typeof dmUnreadResponseSchema>;

/** Preview cap for {@link dmLastMessageSchema.shape.body} — a chat-list line,
 * not the message (the server truncates by CODE POINTS so emoji stay whole). */
export const DM_PREVIEW_MAX_CHARS = 120;

/**
 * `GET /me/dm-summary` — one row per EXISTING conversation (read or fully
 * read alike), newest-first, so the chat list can render a last-message
 * preview + unread count per thread. Superset of `/me/dm-unread` (kept for
 * older bundles): `unread` may be 0 here, and `last` is the newest visible
 * message — `kind:"voice"` carries an empty `body` (clients render a
 * «🎤 پیام صوتی» label instead) and `fromMe` lets the list prefix the
 * caller's own messages («شما: …»). `sentAt` is an ISO timestamp.
 */
export const dmLastMessageSchema = z.object({
  kind: z.enum(["text", "voice"]),
  body: z.string(),
  fromMe: z.boolean(),
  sentAt: z.string(),
});
export type DmLastMessage = z.infer<typeof dmLastMessageSchema>;

export const dmThreadSummarySchema = z.object({
  peerId: z.string(),
  unread: z.number().int().min(0),
  last: dmLastMessageSchema.nullable(),
  /**
   * The peer's leak-free identity (exactly {@link inviteUserSchema}) — what the chat list needs to
   * NAME the row.
   *
   * ⚠️ WHY THIS IS HERE. Without it a summary row is just a `peerId`, so the client could only
   * render conversations whose peer it could name from somewhere else — and its only naming source
   * was the friends list. The گفتگو tab therefore built itself as `friends.filter(hasConversation)`,
   * which means UNFRIENDING SOMEBODY MADE THE WHOLE CONVERSATION DISAPPEAR: the server kept serving
   * the thread on every poll while the client drew «هنوز دوستی نداری». Measured on prod 2026-08-25:
   * 1,201 conversations / 53,973 messages / 781 users in exactly that state.
   *
   * ⚠️ OPTIONAL ON PURPOSE — version skew, not laziness. The web client CASTS this response
   * (`request<DmSummaryResponse>`) instead of parsing it, so a REQUIRED field would be `undefined`
   * at runtime while TypeScript insisted it was there. Absent ⇒ the client falls back to the friends
   * list, i.e. exactly today's behaviour, and never a broken chat tab.
   */
  peer: inviteUserSchema.optional(),
  /**
   * THE CALLER HAS BLOCKED THIS PEER — the chat list's only source for that fact.
   *
   * A blocked thread used to be omitted from this response entirely. It is served now (a block is
   * silent, so the thread stays put for both parties, Telegram-style) and the BLOCKER's composer is
   * replaced by «این کاربر رو مسدود کردی · رفع مسدودی». The client cannot derive it: a blocked peer
   * is in none of `/friends/all`'s three arrays, so the row's `FriendView` is synthesised from
   * `peer` with `status: "none"` and the screen would otherwise draw a live composer over a send
   * the server refuses.
   *
   * ⚠️ EMITTED ONLY WHEN TRUE, and optional for the same reason `peer` is: the web CASTS this
   * response instead of parsing it, and an always-present `false` would move the ETag of every
   * unaffected thread on the deploy that shipped it.
   * ⚠️ THE CONVERSE IS NEVER SENT. There is no «they blocked you» flag and there must not be —
   * that is the disclosure this whole change exists to remove.
   */
  blockedByMe: z.literal(true).optional(),
});
export type DmThreadSummary = z.infer<typeof dmThreadSummarySchema>;

export const dmSummaryResponseSchema = z.object({
  threads: z.array(dmThreadSummarySchema),
});
export type DmSummaryResponse = z.infer<typeof dmSummaryResponseSchema>;

/**
 * `GET /me/dm/:peerId/history` — one PAGE of a conversation, newest-first-fetched
 * but returned in reading order (oldest → newest), ready to render.
 *
 * ⚠️ WHY THIS EXISTS OVER HTTP AT ALL, when the DM room already serves history on join.
 * Opening a conversation used to require a full Colyseus room join — matchmake, WebSocket
 * upgrade, `onAuth`, `onJoin` — before a single message could be drawn, and on the Iranian
 * mobile paths this app serves, the WS UPGRADE is the documented stall (the HTTP matchmake
 * is fast; the upgrade is not). The database side was never the cost: the same query measures
 * **0.63 ms** on the largest real conversation (1,064 messages) via the existing
 * `(conversationId, createdAt)` index. So the thread now paints from ONE authenticated GET
 * and the socket joins behind it, purely for live send/receive.
 *
 * `before` is a keyset cursor — the ISO `sentAt` of the OLDEST message you already hold —
 * so paging up never re-reads what the client has and never drifts as new messages arrive
 * (an OFFSET would shift under exactly that). Omit it for the newest page.
 */
export const dmHistoryMessageSchema = z.object({
  id: z.string(),
  senderId: z.string(),
  kind: z.enum(["text", "voice"]),
  body: z.string(),
  voiceId: z.string().optional(),
  voiceDurationMs: z.number().int().positive().optional(),
  /** Epoch millis — the same shape the DM room puts on the wire, so the two merge by id. */
  at: z.number().int(),
});
export type DmHistoryMessage = z.infer<typeof dmHistoryMessageSchema>;

export const dmHistoryResponseSchema = z.object({
  /** Oldest → newest, ready to render. */
  messages: z.array(dmHistoryMessageSchema),
  /** True when older messages exist beyond this page — drives the scroll-up loader. */
  hasMore: z.boolean(),
});
export type DmHistoryResponse = z.infer<typeof dmHistoryResponseSchema>;

/** Page size for a conversation history request (and the server's hard ceiling). */
export const DM_HISTORY_PAGE = 30;
export const DM_HISTORY_MAX_PAGE = 100;

/**
 * How many conversations the chat list renders before «بیشتر».
 *
 * The list is built from the caller's friends joined to `/me/dm-summary` (itself capped at 200
 * server-side), and today the worst real account has 17 conversations and 48 friends. This is a
 * SCALE GUARD, not a fix for something anyone currently feels: the platform has been adding
 * thousands of accounts a day, and a list that renders every row is the kind of thing that is
 * fine until it suddenly is not.
 */
export const DM_LIST_PAGE = 20;
